import React from "react";
import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";

const ORANGE = "#D67C2C";

/**
 * Four card levels, each clearly different from the one inside it:
 *   1. SectionCard  – a Term: white, soft shadow, gradient icon tile (lesson-plan section style).
 *   2. UnitCard     – a Unit: tinted slate panel with a blue unit badge, no heavy borders.
 *   3. ChapterCard  – a Chapter: white raised card, status dot, highlights when open.
 *   4. FieldGroup   – one question: flat soft-gray block inside the chapter.
 */

export function SectionCard({ icon: Icon, title, subtitle, badge, open, onToggle, children }) {
  return (
    <section className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center gap-4 bg-gradient-to-r from-[#F7FAFF] to-white px-6 py-5 text-left ${open ? "border-b border-gray-100" : ""}`}
      >
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-sm"
          style={{ background: `linear-gradient(135deg, #E8913A, ${ORANGE})` }}
        >
          <Icon className="h-5 w-5 text-white" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-[poppins] text-xl font-semibold text-gray-900">{title}</span>
          {subtitle && <span className="mt-0.5 block font-[poppins] text-sm text-gray-500">{subtitle}</span>}
        </span>
        {badge}
        <ChevronDown className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-6">{children}</div>}
    </section>
  );
}

export function UnitCard({ header, children }) {
  return (
    <div className="mb-5 overflow-hidden rounded-xl bg-slate-50 ring-1 ring-slate-200">
      <div className="border-b border-slate-200/70 px-4 py-3">{header}</div>
      <div className="space-y-3 p-4">{children}</div>
    </div>
  );
}

export function ChapterCard({ id, ready, open, onToggle, title, meta, actions, children }) {
  return (
    <div
      id={id}
      className={`overflow-hidden rounded-xl bg-white shadow-sm transition-shadow ${
        open ? "shadow-md ring-2 ring-blue-200" : "ring-1 ring-gray-200 hover:shadow-md"
      }`}
    >
      <div className="flex items-center gap-1 pr-3">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 text-left">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${ready ? "bg-emerald-500" : "bg-amber-400"}`} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-[poppins] text-base font-semibold text-gray-900">{title}</span>
            <span className={`block truncate font-[poppins] text-sm ${ready ? "text-gray-500" : "text-amber-700"}`}>{meta}</span>
          </span>
          <span
            className={`hidden shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold sm:inline ${
              ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
            }`}
          >
            {ready ? "Ready" : "Needs details"}
          </span>
          {open ? <ChevronDown className="h-5 w-5 shrink-0 text-gray-400" /> : <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />}
        </button>
        {actions}
      </div>
      {open && <div className="space-y-3 border-t border-gray-100 p-4">{children}</div>}
    </div>
  );
}

export function FieldGroup({ label, help, done, children }) {
  return (
    <div className="rounded-xl bg-gray-50 p-4">
      <label className="flex items-center gap-2 font-[poppins] text-base font-semibold text-gray-900">
        {done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <ChevronRight className="h-5 w-5" style={{ color: ORANGE }} />}
        {label}
      </label>
      {help && <p className="mb-3 mt-0.5 pl-7 font-[poppins] text-sm text-gray-500">{help}</p>}
      <div className={help ? "" : "mt-3"}>{children}</div>
    </div>
  );
}
